// ════════════════════════════════════════════════════════════════════════════
//  The devices on an account
//  ---------------------------------------------------------------------------
//  Before this, a device knew exactly one device: itself. The server knows
//  credentials, not machines, and the room knows only who is awake right now.
//  So "your devices" was not a question anything could answer, and Settings had
//  nothing to list.
//
//  THE ROSTER IS A DOCUMENT, not a server table, because that is how everything
//  else on this account works: it is written locally, it syncs through the one
//  chokepoint, and it merges on the device. A table would have made the roster
//  the one thing about you that only Cloudflare could tell you.
//
//  EACH DEVICE OWNS ITS OWN ROW AND NOBODY ELSE'S. The merge is per entry, last
//  write wins on the row's own stamp — so two devices enrolling at once keep
//  both rows, and renaming one from the other is a normal, converging edit
//  rather than a conflict over one blob. It is the same shape `record-status`
//  uses, and for the same reason.
//
//  A NAME HERE IS A GUESS UNTIL SOMEBODY CORRECTS IT. Nothing can tell a phone
//  from a small laptop window from a tablet in landscape, so the default is
//  admittedly a guess — and that is only acceptable because the row can be
//  renamed. Ship the guess, keep the correction one tap away.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  const MD_DEVICES = 'md:devices';

  type Row = { name: string; at: number; gone?: true };

  /* Shown in a list beside a live mark. A newline or a thousand characters is a
     row that eats the section, so a name is one line and bounded — on the way
     IN as well as on the way out, because the way in includes another device. */
  const MAX_NAME = 48;
  /* A corrupt or hostile document must not become an unbounded list. Nobody has
     sixty-four devices; somebody whose document is broken has one row too many
     rather than a Settings page that never finishes. */
  const MAX_DEVICES = 64;

  function tidy(name: unknown): string {
    if (typeof name !== 'string') return '';
    return name.replace(/\s+/g, ' ').trim().slice(0, MAX_NAME);
  }

  /** One row, or null. Exported shape: { name, at } — plus `gone` on a row that
      has been removed, which is a tombstone rather than an absence. See
      mdDeviceRemove for why a removal cannot simply be a missing key. */
  function row(value: any): Row | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const name = tidy(value.name);
    if (!name) return null;
    /* `at` is what decides a conflict between two copies of one row. A row
       without one would win or lose by arrival order, which is not a rule. */
    if (!Number.isFinite(value.at)) return null;
    /* Narrowed to the literal `true` on the way in. A tombstone is a claim that
       has to survive a merge, so "anything truthy" is not good enough: this
       arrives from another device, and the shape is the contract. */
    return value.gone === true ? { name, at: value.at, gone: true } : { name, at: value.at };
  }

  /** The whole roster, as this device holds it — INCLUDING the rows that have
      been removed. This is the document: it is what syncs, what merges, and
      what enrolment consults. A list drawn for a person wants mdDevicesLinked
      below instead. */
  function mdDevicesRead(): Record<string, Row> {
    const held = typeof g.mdRead === 'function' ? g.mdRead(MD_DEVICES, null) : null;
    return mdDevicesSanitise(held) || {};
  }

  /** The devices that are actually on this account — what Settings draws. */
  function mdDevicesLinked(): Record<string, Row> {
    const out: Record<string, Row> = {};
    for (const [id, entry] of Object.entries(mdDevicesRead())) {
      if (!entry.gone) out[id] = entry;
    }
    return out;
  }

  /** What may be written down, whoever said it. Untrusted input by definition:
      the other side of this is another device, and one of them may be older,
      broken, or running a build nobody remembers. */
  function mdDevicesSanitise(value: unknown): Record<string, Row> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    const out: Record<string, Row> = {};
    let n = 0;
    for (const [id, entry] of Object.entries(value as any)) {
      if (!g.mdDeviceIsId(id)) continue;
      const kept = row(entry);
      if (!kept) continue;
      if (++n > MAX_DEVICES) break;
      out[id] = kept;
    }
    return out;
  }

  /** Two rosters into one, row by row.

      Never blob-wise. Each device writes only its own row, so the union is
      always right and the only question is which copy of a shared row is newer
      — which is what `at` is for. A rename made on the desk and an enrolment
      made on the phone are both kept. */
  function mdMergeDevices(local: unknown, remote: unknown): Record<string, Row> {
    const out: Record<string, Row> = {};
    for (const src of [mdDevicesSanitise(local) || {}, mdDevicesSanitise(remote) || {}]) {
      for (const [id, entry] of Object.entries(src)) {
        const seen = out[id];
        /* Ties go to the incumbent, so a merge run twice gives the same answer
           — which is what makes an arriving frame idempotent. */
        if (!seen || entry.at > seen.at) out[id] = entry;
      }
    }
    return out;
  }

  /** What this device offers on a push. Absent until somebody has enrolled, so
      an account that has never been live carries no document at all. */
  function mdDevicesSyncDocs(): Record<string, any> {
    const held = mdDevicesRead();
    return Object.keys(held).length ? { devices: held } : {};
  }

  /** A roster arriving from another device. Merged, never written over: this
      device's own row exists HERE and may not have been pushed yet, and the
      incoming copy is the only place the OTHER device's row exists. */
  function mdDevicesApplyDoc(key: string, value: unknown): boolean {
    if (key !== 'devices') return false;
    if (value == null) return true;          // an empty roster tells us nothing
    const merged = mdMergeDevices(mdDevicesRead(), value);
    return !!g.mdWrite(MD_DEVICES, merged);
  }

  /** The pull loop asks before it refuses an incoming document as a conflict.
      This one merges, so refusing it would leave the other device's row
      unreachable for as long as this device had an unpushed edit of its own —
      which, since this device writes its own row, is most of the time. */
  function mdDevicesMergesDoc(key: string): boolean {
    return key === 'devices';
  }

  // ── this device's own row ─────────────────────────────────────────────────

  /* What the browser will admit about the machine it is on. Read here, passed
     as plain data, so the guess below is provable without a device. */
  function mdDeviceHints(): { touch: boolean; width: number } {
    const nav = g.navigator || {};
    const touch = Number(nav.maxTouchPoints) > 0;
    /* The SHORT side. A phone held sideways is 844 points wide and still a
       phone; the narrow edge is the one that does not turn. */
    const w = Number(g.innerWidth) || 0;
    const h = Number(g.innerHeight) || 0;
    return { touch, width: w && h ? Math.min(w, h) : (w || h) };
  }

  /* Where a phone stops being a phone. A phone's short side is around 390
     points and a tablet's around 768, so the line has room on both sides of it
     — which is as much precision as this question has. */
  const TABLET_FROM = 600;

  /** The name a device gives itself. A GUESS, and it says so: nothing can tell
      a phone from a small laptop window from a tablet held sideways. It is
      shipped anyway because the row can be renamed, and a wrong word one tap
      from being corrected is better than a list with a blank in it. */
  function mdDeviceNameGuess(hints?: { touch?: boolean; width?: number }): string {
    if (!hints || !hints.touch) return 'Computer';
    const width = Number(hints.width) || 0;
    /* No width at all is not a phone claim worth making. */
    if (!width) return 'Computer';
    return width < TABLET_FROM ? 'Phone' : 'Tablet';
  }

  function stamp(now?: number): number {
    return Number.isFinite(now as number) ? (now as number) : Date.now();
  }

  /** Is this that device? Asked by the list, so it can say which row is here. */
  function mdDeviceIsThis(id: unknown): boolean {
    return !!id && typeof g.mdDeviceId === 'function' && id === g.mdDeviceId();
  }

  /** Put this device in the roster, once.

      Called at the moment it joins the account's room, which is the moment it
      is actually linked. A device already in the roster is left EXACTLY as it
      is: rewriting the row would move the revision and offer the document
      again, so a fact that has not changed would cost a push per launch — and
      it would replace a name somebody typed with a guess. */
  function mdDeviceEnrol(now?: number): string {
    const id = typeof g.mdDeviceId === 'function' ? g.mdDeviceId() : '';
    /* No id, no row. A row nobody can be attributed to is worse than no row —
       see app/md-device.ts on why an invented name would be worse still. */
    if (!id) return '';
    const held = mdDevicesRead();
    const seen = held[id];
    if (seen && !seen.gone) return id;
    if (seen) {
      /* A DEVICE THAT WAS REMOVED AND HAS COME BACK. Removing a device ends its
         session; it does not revoke the passkey (server/auth.js says why), so
         it may sign in again — and signing in is what brings it here. The
         tombstone is lifted rather than the row rebuilt, so the name somebody
         typed survives being removed and re-added.

         The stamp must beat the tombstone it is undoing, for the same reason a
         rename must: on a clock behind the device that removed it, a plain
         `now` would lose the merge and this device would keep vanishing from
         the list it had just rejoined. */
      held[id] = { name: seen.name, at: Math.max(stamp(now), seen.at + 1) };
      g.mdWrite(MD_DEVICES, held);
      return id;
    }
    held[id] = { name: mdDeviceNameGuess(mdDeviceHints()), at: stamp(now) };
    g.mdWrite(MD_DEVICES, held);
    return id;
  }

  /** Take a device off the account.

      A TOMBSTONE, NOT A DELETION. Two rosters become one by union
      (mdMergeDevices), so a row simply removed here comes straight back from
      the next device that pulls — its copy still holds the row, its copy is not
      wrong about anything, and a union keeps whatever either side has. So the
      removal is written down as a fact with a stamp, and wins the way every
      other edit here wins.

      THIS IS THE LOCAL HALF ONLY. What makes the removal mean something is the
      other device's session ending, which is a server fact —
      `/api/auth/devices/end`, reached through mdDeviceRevoke in md-sync.js. A
      row that disappeared from a list while the machine it named went on
      reading the account would be a promise kept nowhere. */
  function mdDeviceRemove(id: unknown, now?: number): boolean {
    if (!g.mdDeviceIsId(id)) return false;
    const held = mdDevicesRead();
    const seen = held[id as string];
    if (!seen) return false;                       // nothing has enrolled as that
    if (seen.gone) return true;                    // already, and nothing to write
    held[id as string] = { name: seen.name, at: Math.max(stamp(now), seen.at + 1), gone: true };
    return !!g.mdWrite(MD_DEVICES, held);
  }

  /** Rename a device — ANY device on the account, not only this one. The desk
      naming the phone is what the list is for; a machine cannot be asked to
      name itself while it is in a drawer.

      What stays true is the other direction: enrolment never writes over a
      name, so nothing anybody typed is ever replaced by a guess. */
  function mdDeviceRename(id: unknown, name: unknown, now?: number): boolean {
    if (!g.mdDeviceIsId(id)) return false;
    const clean = tidy(name);
    /* Clearing a name would un-name the device rather than reset it, and the
       roster refuses a nameless row — so the row would simply disappear. */
    if (!clean) return false;
    const held = mdDevicesRead();
    const seen = held[id as string];
    if (!seen) return false;                       // nothing has enrolled as that
    /* And a removed device is not a device on this account. Nothing offers this
       — a rename is reached by tapping a row, and a removed row is not in the
       list — but naming one would write a row with no `gone` on it and quietly
       bring it back, which is a worse failure than refusing. */
    if (seen.gone) return false;
    if (seen.name === clean) return true;          // nothing to write, nothing to push
    /* THE STAMP MUST BEAT THE ROW IT REPLACES. It comes from a client clock, and
       a device whose clock is behind the one that wrote this row would stamp the
       rename lower than the incumbent — so the merge would hand the old name
       straight back and the rename would read as having been ignored. */
    held[id as string] = { name: clean, at: Math.max(stamp(now), seen.at + 1) };
    return !!g.mdWrite(MD_DEVICES, held);
  }

  Object.assign(g, {
    MD_DEVICES_MAX_NAME: MAX_NAME,
    mdDevicesRead, mdDevicesLinked, mdDevicesSanitise, mdMergeDevices,
    mdDevicesSyncDocs, mdDevicesApplyDoc, mdDevicesMergesDoc,
    mdDeviceHints, mdDeviceNameGuess, mdDeviceIsThis, mdDeviceEnrol, mdDeviceRename,
    mdDeviceRemove,
  });
})(typeof window !== 'undefined' ? window : globalThis);
